import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import { useContext } from "react";
import { CommandCenterStateProviderContext } from "../context";
import CommandCenterService from "../../../service/CommandCenterService";
import { showToastMessage } from "../../../utils/commonFunctions";
import useTrueTwin from "./useTrueTwin";
import useUrlParams from "./useUrlParams";
import { v4 as uuidv4 } from 'uuid';
dayjs.extend(isToday);
dayjs.extend(isYesterday);

const preserveProgressArray = ['Uplinking',
    'Error',
    'Suspended',
    'Suspend Inprogress',
    'Resume Inprogress',
    'Unknown'
]

const useFiles = () => {
    const context = useContext(CommandCenterStateProviderContext);
    if (context === undefined) {
        throw new Error('useFiles must be used within a Provider');

    }
    const { allACPStorageFiles, setAllACPStorageFiles, setAllDestinationIds, allDestinationIds, isGroundFilesLoading, setIsGroundFilesLoading, setProgressOfSatUpload, allOnboardFiles, setAllOnboardFiles, allDownlinkingFiles, setAllDownlinkingFiles, isDownlinkqueueLoading, setIsDownlinkqueueLoading, downlinkHistoryFiles, setDownlinkHistoryFiles, isOnboardFilesLoading, setIsOnboardFilesLoading, downlinkRefreshTime, setDownlinkRefreshTime, isRefreshTimeLoading,
        setIsRefreshTimeLoading } = context;
    const { truetwin_id } = useTrueTwin();
    const { scenario_id, mission_id } = useUrlParams();
    const resolveTrueTwinId = (id?: string) => id ?? truetwin_id ?? null;

    //GET ALL MINIO STORAGE FILES
    const getAllMinioStorageFiles = async (id?: string) => {
        const trueId = resolveTrueTwinId(id);
        if (!trueId) return;
        try {
            setIsGroundFilesLoading(true)
            const response = await CommandCenterService.getACPFiles(mission_id, scenario_id, trueId)
            const filesObject = response?.data;

            if (filesObject && Object.keys(filesObject).length > 0) {
                setAllACPStorageFiles((prev) => {
                    const updated = {};
                    for (const folder of Object.keys(filesObject)) {
                        const folderItems = filesObject[folder] || [];
                        const prevFolderMap = prev?.[folder] ?? new Map();
                        const updatedMap = new Map(
                            folderItems.map((item: any) => {
                                const prevFile = prevFolderMap.get(item.uniqueId);
                                const formattedLastModified = item?.lastModified?.replace('T', ' ').slice(0, 19) + ' UTC';
                                const shouldPreserveProgress = preserveProgressArray.includes(item?.fileStatus);
                                return [
                                    item.uniqueId,
                                    {
                                        ...item,
                                        lastModified: formattedLastModified,
                                        progress: shouldPreserveProgress ? prevFile?.progress ?? 0 : 0,
                                        fileStatus: item.fileStatus,
                                    },
                                ];
                            })
                        );

                        updated[folder] = updatedMap;
                    }
                    return updated;
                });
            } else {
                setAllACPStorageFiles(null);
            }
        } catch (error: any) {
            showToastMessage('error', error?.response?.data?.message ?? 'Something went wrong')
        } finally {
            setIsGroundFilesLoading(false)
        }
    }

    //GET LIST OF ALL DESTINATION IDS
    const getAllDestinationIds = async () => {
        try {
            const response = await CommandCenterService.getDestinationIds();
            if (response && response?.data?.length > 0) {
                const convertedData = response?.data.map((item) => ({
                    label: item?.Label,
                    value: item,
                }));

                setAllDestinationIds(convertedData);
            } else {
                setAllDestinationIds(null)
            }
        } catch (error: any) {
            showToastMessage('error', error?.response?.data?.message ?? 'Something went wrong while getting destination ids')
        }
    }

    //CANCEL ONGOING UPLOAD TO SATELLITE
    const cancelUploadToSatellite = async (id) => {
        try {
            const response = await CommandCenterService?.cancelUploadingGroundfile(truetwin_id, id);
            if (response?.data) {
                showToastMessage('success', response?.data?.message ?? 'File uplink cancelled')
            }

        } catch (error: any) {
            showToastMessage('error', error?.response?.data?.message ?? 'Something went wrong while cancelling file uplink')
        }
    }

    //REMOVE FILE NAME FROM LOCAL STORAGE -> (MINIO UPLOADING FILES)
    const removeFromLocalStorage = (id) => {
        const existing = JSON.parse(localStorage.getItem('uploadedFiles') || '[]');
        const updated = existing.filter((name) => name?.id !== id);
        localStorage.setItem('uploadedFiles', JSON.stringify(updated));
    };

    //POST FILE TO MINIO STORAGE
    const postFileToMinio = async (file) => {
        const fileName = file.name;
        const fileSize = file.size;
        const fileId = uuidv4();

        try {
            if (fileName.includes(' ')) {
                showToastMessage(
                    'error',
                    'File name should not contain spaces. Please rename the file and try again.'
                );
                return;
            }
            // Step 1: Save file info to localStorage before uploading
            const existing = JSON.parse(localStorage.getItem('uploadedFiles') || '[]');
            const newFile = {
                id: fileId,
                fileName: fileName,
                fileSize: fileSize,
                status: 'uploading'
            };
            localStorage.setItem('uploadedFiles', JSON.stringify([...existing, newFile]));

            // Step 2: Prepare for chunked upload
            const chunkSize = 10 * 1024 * 1024; // 10MB
            const totalChunks = Math.ceil(file.size / chunkSize);

            for (let i = 0; i < totalChunks; i++) {
                const start = i * chunkSize;
                const end = Math.min(file.size, start + chunkSize);
                const chunk = file.slice(start, end);

                const formData = new FormData();
                formData.append('fileName', fileName);
                formData.append('chunkNum', i.toString());
                formData.append('totalChunk', totalChunks.toString());
                formData.append('file', chunk);

                try {
                    await CommandCenterService.postFileToACP({
                        missionId: mission_id,
                        truetwinId: truetwin_id,
                        scenarioId: scenario_id,
                        formData
                    });
                } catch (err: any) {
                    showToastMessage('error', err?.response?.data?.message ?? 'Upload Failed');
                    removeFromLocalStorage(newFile?.id); // Remove the failed file entry
                    return;
                }
            }

            // Step 3: Update status to 'complete'
            const afterUpload = JSON.parse(localStorage.getItem('uploadedFiles') || '[]');
            const updated = afterUpload.map((item: any) =>
                item.id === fileId ? { ...item, status: 'complete' } : item
            );
            localStorage.setItem('uploadedFiles', JSON.stringify(updated));

            // Step 4: Remove file after 5 seconds
            setTimeout(() => {
                const latest = JSON.parse(localStorage.getItem('uploadedFiles') || '[]');
                const cleaned = latest.filter((item: any) => item.id !== fileId);
                localStorage.setItem('uploadedFiles', JSON.stringify(cleaned));
            }, 5000);

            // Step 5: Refresh UI
            getAllMinioStorageFiles();
            showToastMessage('success', 'File uploaded successfully')
        } catch (error) {
            console.error(error);
            showToastMessage('error', "Upload Failed");
            removeFromLocalStorage(file.name); // Also remove on unexpected error
        }
    }

    //DELETE FILE FROM MINIO STORAGE
    const deleteFileFromMinio = async (id) => {
        try {
            setIsGroundFilesLoading(true)
            const response = await CommandCenterService.deleteACPFile(mission_id, scenario_id, truetwin_id, id);
            if (response?.data) {
                showToastMessage('success', response?.data?.message ?? 'File deleted successfully')
                getAllMinioStorageFiles()
            }

        } catch (error: any) {
            showToastMessage('error', error?.response?.data?.message ?? 'Delete failed')
            setIsGroundFilesLoading(false)
        }
    }

    const pauseTheUplinkingFile = async (id) => {
        try {
            const response = await CommandCenterService.pauseGroundFileTransfer(truetwin_id, id)
            if (response?.data) {
                showToastMessage('success', response?.data?.message ?? 'File uplink paused')
                getAllMinioStorageFiles()
            }

        } catch (error: any) {
            showToastMessage('error', error?.response?.data?.message ?? 'Pause failed')
            setIsGroundFilesLoading(false)
        }
    }

    const ResumeThePausedFile = async (id) => {
        try {
            const response = await CommandCenterService.resumeGroundFileTransfer(truetwin_id, id)
            if (response?.data) {
                showToastMessage('success', response?.data?.message ?? 'Resumed successfully')
                getAllMinioStorageFiles()
            }

        } catch (error: any) {
            showToastMessage('error', error?.response?.data?.message ?? 'Resume failed')
            setIsGroundFilesLoading(false)
        }
    }

    const downloadFileToLocal = async (fileId) => {
        try {
            const response = await CommandCenterService.downloadFromMinio(
                mission_id,
                scenario_id,
                truetwin_id,
                fileId
            );

            if (response) {
                const downloadUrl = window.URL.createObjectURL(response.data);

                // Extract the filename from Content-Disposition
                const contentDisposition = response.headers['content-disposition'];
                let fileName = "downloaded-file";

                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (fileNameMatch && fileNameMatch[1]) {
                        fileName = fileNameMatch[1];
                    }
                }

                // Create download link
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);
                showToastMessage('success', 'File downloaded successfully');
            }

        } catch (error: any) {
            showToastMessage('error', 'Download failed');
        }
    }

    ////////------------------------------------------------ONBOARD FILES--------------------------------------------------------/////

    //GET ALL ONBOARD FILES
    const getAllOnboardFiles = async (id?: string) => {
        const trueId = resolveTrueTwinId(id);
        if (!trueId) return;
        try {
            setIsOnboardFilesLoading(true)
            const response = await CommandCenterService.getOnboardFileList(mission_id, scenario_id, trueId)

            if (response?.data && response?.data.length > 0) {
                const payloadMap: Record<string, Map<string, any>> = {};

                response?.data.forEach((item: any) => {
                    const payloadKey = item.folderName; // folder name becomes the key
                    const payloadArray = item.data;     // files array

                    if (Array.isArray(payloadArray)) {
                        // Convert each payload array to a Map keyed by uniqueId
                        const mapForPayload = new Map<string, any>(
                            payloadArray.map((fileItem: any) => {
                                return [
                                    fileItem.uniqueId,
                                    {
                                        ...fileItem,
                                        updatedAt: fileItem.updatedAt?.replace('T', ' ').slice(0, 19) + ' UTC',
                                        status: fileItem.status,
                                    },
                                ];
                            })
                        );

                        payloadMap[payloadKey] = mapForPayload;
                    }
                });
                setAllOnboardFiles(payloadMap);
            } else {
                setAllOnboardFiles(null);
            }
        } catch (error: any) {
            showToastMessage('error', error?.response?.data?.message ?? 'Something went wrong')
        } finally {
            setIsOnboardFilesLoading(false)
        }
    }

    //GET DOWNLINK QUEUE
    const getDownlinkQueue = async (id?: string) => {
        const trueId = resolveTrueTwinId(id);
        if (!trueId) return;
        try {
            setIsOnboardFilesLoading(true)
            const response = await CommandCenterService.getDownlinkQueue(mission_id, scenario_id, trueId)

            if (response?.data) {
                setAllDownlinkingFiles((prev) => {
                    const convertedDataMap = new Map<string, any>(
                        response?.data.map((item) => {
                            const prevFile = prev?.get(item?.uniqueId);
                            return [
                                item.uniqueId,
                                {
                                    ...item,
                                    updatedAt: item?.updatedAt?.replace('T', ' ').slice(0, 19) + ' UTC', //convert date to utc format
                                    progress: item?.status === "download_inprogress" ? prevFile?.progress ?? 0 : 0, // keep old progress if available
                                    status: item?.status, //update new status
                                }
                            ];
                        })
                    );
                    return convertedDataMap;
                });
            } else {
                setAllDownlinkingFiles(null)
            }
            setIsOnboardFilesLoading(false)
        }
        catch (error: any) {
            setIsOnboardFilesLoading(false)
            showToastMessage('error', error?.response?.data?.message ?? 'Something went wrong while getting downlink queue')
        }
    }

    //REFRESH THE DOWNLINK QUEUE
    const refreshQueue = async (id?: string) => {
        const trueId = resolveTrueTwinId(id);
        if (!trueId) return;
        try {
            setIsDownlinkqueueLoading(true)
            setIsOnboardFilesLoading(true)
            const res = await CommandCenterService.refreshDownlinkQueue(mission_id, trueId, scenario_id)
            if (res?.data) {
                // Wait for 5 seconds before making the next API calls
                setTimeout(async () => {
                    try {
                        await Promise.all([
                            getDownlinkQueue(trueId),
                            getDownlinkRefreshTime(),
                            getAllOnboardFiles(trueId),
                        ]);
                        showToastMessage('success', 'Refreshed Status Successfully');
                    } finally {
                        setIsDownlinkqueueLoading(false);
                        setIsOnboardFilesLoading(false);
                    }
                }, 5000); // delay in ms
            }
        } catch (error: any) {
            setIsDownlinkqueueLoading(false)
            setIsOnboardFilesLoading(false)
            showToastMessage('error', error?.response?.data?.message ?? 'Something went wrong while refreshing')
        }
    }

    //CHANGE ONBOARD PRIORITY
    const onBoardChangePriority = async (fileId: string, priority: string, id?: string) => {
        const trueId = resolveTrueTwinId(id);
        if (!trueId) return;

        try {
            setIsDownlinkqueueLoading(true);
            setIsOnboardFilesLoading(true)
            const res = await CommandCenterService.changePriority(
                mission_id,
                trueId,
                scenario_id,
                fileId,
                priority
            );

            if (res?.data) {
                // Run both calls at the same time
                await Promise.all([
                    getDownlinkQueue(trueId),
                    getAllOnboardFiles(trueId),
                ]);
                showToastMessage('success', 'Priority Updated Successfully')
            }
        } catch (error: any) {
            showToastMessage(
                "error",
                error?.response?.data?.message ?? "Something went wrong while changing priority"
            );
        } finally {
            setIsDownlinkqueueLoading(false);
            setIsOnboardFilesLoading(false)
        }
    };

    //DELETE ONBOARD FILE
    const deleteDownloadFile = async (fileId: string, id?: string) => {
        const trueId = resolveTrueTwinId(id);
        if (!trueId) return;

        try {
            setIsDownlinkqueueLoading(true);
            setIsOnboardFilesLoading(true)
            const res = await CommandCenterService.deleteDownlink(
                mission_id,
                trueId,
                scenario_id,
                fileId
            );

            if (res) {
                // Run both API calls concurrently
                await Promise.all([
                    getDownlinkQueue(trueId),
                    getAllOnboardFiles(trueId),
                ]);
            }
            showToastMessage('success', 'Deleted Onboard Successfully')
        } catch (error: any) {
            showToastMessage(
                "error",
                error?.response?.data?.message ?? "Something went wrong while deleting"
            );
        } finally {
            setIsDownlinkqueueLoading(false);
            setIsOnboardFilesLoading(false)
        }
    };

    //GET ONBOARD HISTORY
    const getOnboardHistory = async (id?: string) => {
        const trueId = resolveTrueTwinId(id);
        if (!trueId) return;

        try {
            setIsDownlinkqueueLoading(true);
            const res = await CommandCenterService.getDownlinkHistory(mission_id, trueId, scenario_id);

            if (res?.data) {
                // Convert array to map keyed by UniqueId
                const mappedData = new Map<string, any>(
                    res.data.map((item: any) => [item.uniqueId, item])
                );

                // Group files by date: Today, Yesterday, and past dates
                const groupedByDate = new Map<string, any[]>();

                mappedData.forEach(file => {
                    const fileDate = dayjs(file.updatedAt);
                    let dateKey: string;

                    if (fileDate.isToday()) dateKey = 'Today';
                    else if (fileDate.isYesterday()) dateKey = 'Yesterday';
                    else dateKey = fileDate.format('YYYY-MM-DD');

                    if (!groupedByDate.has(dateKey)) groupedByDate.set(dateKey, []);
                    groupedByDate.get(dateKey)?.push(file);
                });

                setDownlinkHistoryFiles(groupedByDate);
            }
            setIsDownlinkqueueLoading(false);
        } catch (error: any) {
            setIsDownlinkqueueLoading(false);
            showToastMessage('error', error?.response?.data?.message ?? 'Something went wrong while fetching history');
        }
    };

    //GET REFRESH DOWNLINK TIME
    const getDownlinkRefreshTime = async (id?: string) => {
        const trueId = resolveTrueTwinId(id);
        if (!trueId) return;
        try {
            setIsRefreshTimeLoading(true)
            const res = await CommandCenterService.getLastRefreshTime(trueId);
            if (res?.data) {
                setDownlinkRefreshTime(res?.data)
            } else {
                setDownlinkRefreshTime(null)
            }
        } catch (error: any) {
            setDownlinkRefreshTime(null)
        } finally {
            setIsRefreshTimeLoading(false)
        }
    }

    return {
        allACPStorageFiles,
        setAllACPStorageFiles,
        getAllMinioStorageFiles,
        postFileToMinio,
        deleteFileFromMinio,
        getAllDestinationIds,
        allDestinationIds,
        isGroundFilesLoading,
        setIsGroundFilesLoading,
        cancelUploadToSatellite,
        setProgressOfSatUpload,
        pauseTheUplinkingFile,
        ResumeThePausedFile,
        downloadFileToLocal,
        allOnboardFiles,
        setAllOnboardFiles,
        getAllOnboardFiles,
        getDownlinkQueue,
        allDownlinkingFiles,
        setAllDownlinkingFiles,
        refreshQueue,
        isDownlinkqueueLoading,
        onBoardChangePriority,
        deleteDownloadFile,
        getOnboardHistory,
        downlinkHistoryFiles,
        isOnboardFilesLoading,
        setIsOnboardFilesLoading,
        getDownlinkRefreshTime,
        isRefreshTimeLoading,
        downlinkRefreshTime
    }
}

export default useFiles;